import React, { useEffect, useState } from "react";

import { useFetchBiInventoryQuery, useFetchBiInventoryVariantsQuery } from "../../../api/analyticsApi";
import { Chip, Select, Segmented, TextInput } from "../../catalogProductStudio/edit/StudioUi";
import { BarList, DataNotes, DataState, DataTable, ExportButton, KpiCard, Pager, Panel, ProductCell } from "../AnalyticsUi";
import { familyLabel, fill, formatDate, formatMoney, formatNumber, formatPercent } from "../analyticsModel";

const FLAG_TONE = { out: "danger", low: "warning", dead: "danger", slow: "warning", high_demand: "info" };
const STATUS_FILTERS = ["all", "high_demand", "low", "out", "slow", "dead", "in_stock"];

const Flags = ({ flags, tt }) => (
  <span className="an-chips is-inline">
    {flags
      .filter((flag) => flag !== "in_stock")
      .map((flag) => (
        <Chip key={flag} tone={FLAG_TONE[flag]}>
          {tt(`flag_${flag}`)}
        </Chip>
      ))}
  </span>
);

const variantColumns = ({ tt, lang, costVisible, compact }) => [
  {
    key: "variant",
    label: tt("col_variant"),
    className: "an-col-product",
    render: (row) => (
      <ProductCell
        tt={tt}
        productId={row.productId}
        imageUrl={row.imageUrl}
        title={row.productTitle}
        sub={[row.color, row.size, compact ? null : row.sku].filter(Boolean).join(" · ")}
      />
    ),
  },
  { key: "available", label: tt("col_available"), sortKey: "onHand", align: "end", render: (row) => (
    <span className="an-cell-stack is-end">
      <strong>{formatNumber(row.available, lang)}</strong>
      {row.reserved ? <span className="an-muted">{fill(tt("unitsOnHandHint"), { reserved: row.reserved })}</span> : null}
    </span>
  ) },
  { key: "units30", label: tt("col_units30"), sortKey: "units30", align: "end", render: (row) => formatNumber(row.units30, lang) },
  { key: "cover", label: tt("col_cover"), sortKey: "daysOfCover", align: "end", render: (row) => (row.daysOfCover === null ? <span className="an-muted">—</span> : fill(tt("daysShort"), { days: formatNumber(row.daysOfCover, lang) })) },
  ...(compact
    ? []
    : [
        { key: "lastSold", label: tt("col_lastSold"), sortKey: "lastSoldAt", align: "end", render: (row) => (row.lastSoldAt ? formatDate(row.lastSoldAt, lang, { day: "2-digit", month: "short", year: "2-digit" }) : <span className="an-muted">{tt("never")}</span>) },
        { key: "received", label: tt("col_received"), align: "end", render: (row) => (row.lastReceivedAt ? formatDate(row.lastReceivedAt, lang, { day: "2-digit", month: "short", year: "2-digit" }) : <span className="an-muted">{tt("unknownReceipt")}</span>) },
      ]),
  { key: "value", label: tt("retailValue"), sortKey: "retailValue", align: "end", render: (row) => formatMoney(row.retailValue, lang) },
  ...(costVisible && !compact
    ? [{ key: "cost", label: tt("costValue"), sortKey: "costValue", align: "end", render: (row) => (row.costValue === null ? <Chip tone="danger">{tt("costMissingTag")}</Chip> : formatMoney(row.costValue, lang)) }]
    : []),
  ...(compact ? [] : [{ key: "flags", label: tt("stockHealth"), render: (row) => <Flags flags={row.flags} tt={tt} /> }]),
];

const StockTable = ({ thresholds, tt, lang, costVisible }) => {
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [sort, setSort] = useState("onHand");
  const [direction, setDirection] = useState("desc");
  const [page, setPage] = useState(1);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(search), 300);
    return () => clearTimeout(timer);
  }, [search]);
  useEffect(() => setPage(1), [status, debounced, sort, direction, thresholds.lowStockThreshold, thresholds.deadStockDays]);

  const filters = { ...thresholds, status: status === "all" ? undefined : status, search: debounced || undefined };
  const query = useFetchBiInventoryVariantsQuery({ ...filters, sort, direction, page, pageSize: 25 });

  return (
    <Panel id="an-stock" title={tt("stockTable")} actions={<ExportButton dataset="inventory" params={filters} tt={tt} />}>
      <div className="an-filters">
        <TextInput className="an-filters__search" value={search} onChange={setSearch} placeholder={tt("search")} aria-label={tt("search")} type="search" />
        <Select value={status} onChange={setStatus} aria-label={tt("stockHealth")}>
          {STATUS_FILTERS.map((value) => (
            <option key={value} value={value}>
              {tt(`flag_${value}`)}
            </option>
          ))}
        </Select>
      </div>
      <DataState query={query} tt={tt}>
        {query.data ? (
          <>
            <DataTable
              caption={tt("stockTable")}
              rows={query.data.items}
              rowKey={(row) => row.variantId}
              sort={sort}
              direction={direction}
              onSort={(key) => {
                if (key === sort) setDirection(direction === "desc" ? "asc" : "desc");
                else {
                  setSort(key);
                  setDirection("desc");
                }
              }}
              emptyText={tt("emptyTitle")}
              columns={variantColumns({ tt, lang, costVisible, compact: false })}
            />
            <Pager pagination={query.data.pagination} onPage={setPage} tt={tt} />
          </>
        ) : null}
      </DataState>
    </Panel>
  );
};

const InventorySection = ({ tt, lang, costVisible }) => {
  const [lowStockThreshold, setLow] = useState(2);
  const [deadStockDays, setDead] = useState(90);
  const [view, setView] = useState("categories");
  const thresholds = { lowStockThreshold, deadStockDays };
  const query = useFetchBiInventoryQuery(thresholds);
  const data = query.data;

  return (
    <>
      <div className="an-inline-controls">
        <p className="an-muted an-small">{tt("inventorySnapshot")}</p>
        <label className="an-inline-field">
          <span>{tt("lowStockThreshold")}</span>
          <Select value={String(lowStockThreshold)} onChange={(value) => setLow(Number(value))}>
            {[0, 1, 2, 3, 5].map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </Select>
        </label>
        <label className="an-inline-field">
          <span>{tt("deadStockDays")}</span>
          <Select value={String(deadStockDays)} onChange={(value) => setDead(Number(value))}>
            {[60, 90, 120, 180, 365].map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </Select>
        </label>
      </div>
      <DataState query={query} tt={tt}>
        {data ? (
          <>
            <div className="an-kpis">
              <KpiCard tt={tt} lang={lang} label={tt("unitsOnHand")} display={formatNumber(data.summary.unitsOnHand, lang)} sub={fill(tt("unitsOnHandHint"), { reserved: data.summary.reserved })} />
              <KpiCard tt={tt} lang={lang} label={tt("retailValue")} help={tt("retailValueHelp")} display={formatMoney(data.summary.retailValue, lang)} sub={data.summary.retailCoverage !== null && data.summary.retailCoverage < 1 ? fill(tt("coverage"), { value: formatPercent(data.summary.retailCoverage, lang) }) : null} />
              <KpiCard
                tt={tt}
                lang={lang}
                label={tt("costValue")}
                help={tt("costValueHelp")}
                restricted={!costVisible}
                value={data.summary.costValue}
                display={formatMoney(data.summary.costValue, lang)}
                sub={costVisible && data.summary.costCoverage !== null ? fill(tt("coverage"), { value: formatPercent(data.summary.costCoverage, lang) }) : null}
              />
              <KpiCard
                tt={tt}
                lang={lang}
                label={tt("turnover")}
                help={tt("turnoverHelp")}
                value={data.summary.unitTurnover}
                display={`${formatNumber(data.summary.unitTurnover, lang, 2)}×`}
                sub={data.summary.daysOfInventory !== null ? fill(tt("daysOfInventory"), { days: formatNumber(data.summary.daysOfInventory, lang) }) : null}
              />
              <KpiCard tt={tt} lang={lang} tone="warning" label={tt("flag_high_demand")} help={tt("flag_high_demand_help")} display={formatNumber(data.summary.highDemandLowStock, lang)} />
              <KpiCard tt={tt} lang={lang} tone="warning" label={tt("flag_low")} help={fill(tt("flag_low_help"), { threshold: lowStockThreshold })} display={formatNumber(data.summary.lowStock, lang)} />
              <KpiCard tt={tt} lang={lang} label={tt("flag_out")} display={formatNumber(data.summary.outOfStock, lang)} />
              <KpiCard
                tt={tt}
                lang={lang}
                tone="danger"
                label={tt("flag_dead")}
                help={fill(tt("flag_dead_help"), { days: deadStockDays })}
                display={formatNumber(data.summary.deadStock, lang)}
                sub={`${formatNumber(data.summary.deadStockUnits, lang)} ${tt("units").toLowerCase()} · ${formatMoney(data.summary.deadStockRetailValue, lang)}`}
              />
            </div>
            <DataNotes notes={data.notes} tt={tt} lang={lang} />

            <div className="an-grid an-grid--2">
              <Panel
                id="an-distribution"
                title={tt("distribution")}
                actions={
                  <Segmented
                    ariaLabel={tt("distribution")}
                    value={view}
                    onChange={setView}
                    options={[
                      { value: "categories", label: tt("byCategory") },
                      { value: "sizes", label: tt("bySize") },
                      { value: "families", label: tt("byFamily") },
                      { value: "products", label: tt("byProduct") },
                    ]}
                  />
                }
              >
                <BarList
                  rows={data.distribution[view]}
                  valueKey="units"
                  labelOf={(row) => (row.label ? (view === "families" ? familyLabel(tt, row.label) : row.label) : view === "categories" ? tt("uncategorised") : tt("notSet"))}
                  format={(value, row) => `${formatNumber(value, lang)} ${tt("units").toLowerCase()} · ${formatPercent(row.share, lang)}`}
                  secondary={(row) =>
                    `${tt("retailValue")}: ${formatMoney(row.retailValue, lang)}${
                      costVisible
                        ? ` · ${tt("costValue")}: ${row.costValue === null ? tt("unavailable") : formatMoney(row.costValue, lang)}${
                            row.costCoverage !== null && row.costCoverage < 1 && row.costValue !== null ? ` (${fill(tt("coverage"), { value: formatPercent(row.costCoverage, lang) })})` : ""
                          }`
                        : ""
                    }`
                  }
                  emptyText={tt("emptyTitle")}
                />
              </Panel>
              <Panel id="an-high-demand" title={tt("highDemandList")} hint={tt("flag_high_demand_help")}>
                <DataTable dense rows={data.lists.highDemand} rowKey={(row) => row.variantId} emptyText={tt("insightsEmpty")} columns={variantColumns({ tt, lang, costVisible, compact: true })} />
              </Panel>
            </div>
            <div className="an-grid an-grid--2">
              <Panel id="an-low" title={tt("lowStockList")} hint={fill(tt("flag_low_help"), { threshold: lowStockThreshold })}>
                <DataTable dense rows={data.lists.lowStock} rowKey={(row) => row.variantId} emptyText={tt("insightsEmpty")} columns={variantColumns({ tt, lang, costVisible, compact: true })} />
              </Panel>
              <Panel id="an-dead" title={tt("deadStockList")} hint={fill(tt("flag_dead_help"), { days: deadStockDays })}>
                <DataTable dense rows={data.lists.deadStock} rowKey={(row) => row.variantId} emptyText={tt("insightsEmpty")} columns={variantColumns({ tt, lang, costVisible, compact: true })} />
              </Panel>
            </div>
            <StockTable thresholds={thresholds} tt={tt} lang={lang} costVisible={costVisible} />
          </>
        ) : null}
      </DataState>
    </>
  );
};

export default InventorySection;
